import { createRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { getCurrentUser } from '@/integrations/idb/models/users';
import { rootRoute } from '@/root-route';
import * as dashboard from '@/shared/dashboard';
import { AUTH_ROUTES } from './consts/routes';
import {
  externalEntrySearchSchema,
  parseExternalEntryParams,
} from './utils/external-entry-params';
import {
  stashExternalEntryParams,
  takeExternalEntryParams,
} from './utils/external-entry-params-stash';
import { runExternalEntry } from './utils/run-external-entry';

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.login,
  validateSearch: zodValidator(
    z.object({
      redirect: z
        .string()
        .refine((val) => val.startsWith('/'), 'Must start with /')
        .refine((val) => !val.startsWith('//'), 'Cannot start with //')
        .refine((val) => !val.includes('://'), 'Cannot contain ://')
        .refine(
          (val) => !/[<>"'`]/.test(val),
          'Cannot contain special characters',
        )
        .optional()
        .catch(undefined),
    }),
  ),
  beforeLoad: async ({ search }) => {
    const currentUser = await getCurrentUser();

    if (currentUser?.token) {
      throw redirect({
        to: search.redirect || dashboard.DASHBOARD_ROUTES.index,
        ignoreBlocker: true,
        replace: true,
      });
    }
  },
}).lazy(() => import('./login').then(({ Route }) => Route));

export const unauthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.unauthorized,
}).lazy(() => import('./unauthorized').then(({ Route }) => Route));

export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.resetPassword,
  validateSearch: zodValidator(
    z.object({
      token: z.string().optional().catch(undefined),
    }),
  ),
  beforeLoad: ({ search }) => {
    if (!search.token) {
      throw redirect({ to: AUTH_ROUTES.login, replace: true });
    }
  },
}).lazy(() => import('./reset-password').then(({ Route }) => Route));

export const externalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: AUTH_ROUTES.external,
  validateSearch: zodValidator(externalEntrySearchSchema),
  // Stash the params, replace-redirect to a bare /external to clear the URL, then the loader
  // of that load takes the params and runs the entry.
  beforeLoad: ({ search }) => {
    if (search.token || search.refreshToken || search.page) {
      stashExternalEntryParams(parseExternalEntryParams(search));

      throw redirect({ to: AUTH_ROUTES.external, replace: true });
    }
  },
  loader: async ({ context: { queryClient } }) =>
    match(await runExternalEntry(takeExternalEntryParams(), queryClient))
      .with({ type: 'redirect' }, ({ to }) => {
        throw redirect({ to, replace: true });
      })
      .with(
        { type: P.union('invalidLink', 'identityConflict') },
        (card) => card,
      )
      .exhaustive(),
}).lazy(() => import('./external-entry').then(({ Route }) => Route));
